import { TrainingRow } from "./detectClient";

export const DATASET_14015_NAME = "catchai_default_14015_rows.csv";
export const LEGACY_DATASET_14015_NAME = "veritext_default_14015_rows.csv";
export const DATASET_14015_COUNT = 14015;
export const DATASET_14015_HUMAN_COUNT = 7008;
export const DATASET_14015_AI_COUNT = 7007;

// Human Subject Domains for Authentic Stylometric Synthesis
const HUMAN_DOMAINS = [
  "coastal wetland restoration",
  "early Renaissance fresco techniques",
  "high-altitude alpine meteorology",
  "urban community gardening",
  "cellular mitochondrial dynamics",
  "post-war modernist poetry",
  "indigenous agroforestry practices",
  "algorithmic game theory in auctions",
  "neurological correlates of memory consolidation",
  "deep-sea hydrothermal vent ecologies",
  "architectural acoustics of Romanesque basilicas",
  "macroeconomic supply chain shocks",
  "paleolithic flint knapping methods",
  "renewable microgrid power stabilization",
  "mycorrhizal fungal networks in temperate old-growth forests",
  "historical typography and metal punch-cutting",
  "quantum entanglement telemetry",
  "traditional Japanese timber joinery",
  "vernacular blues instrumentation in the Mississippi delta",
  "soil microbiome respiration rates in arid steppes",
  "hand-thrown stoneware ceramic kiln glazes",
  "glacial ice-core atmospheric gas analysis",
  "bilingual syntactic interference in childhood development",
  "astronomical spectrographic calibration of Cepheid variables",
  "foraging behaviors in social hymenoptera colonies",
  "early copper smelting in the Levant",
  "subsurface karst hydrology and sinkhole morphology",
  "manuscript illumination pigments in medieval monastic scriptoria",
  "computational fluid dynamics around humpback whale flippers",
  "dialectal shifts in maritime fishing communities"
];

const HUMAN_OPENERS = [
  (s: string, n: number) => `When we first began investigating ${s} during trial #${n}, nobody in the department anticipated the erratic baseline shifts.`,
  (s: string, n: number) => `I still recall spending three consecutive rainy evenings in the basement archive reviewing historical logs on ${s} for ledger #${n}.`,
  (s: string, n: number) => `Our field team had barely pitched their canvas shelters when sample #${n} of ${s} arrived with unexpected moisture contamination.`,
  (s: string, n: number) => `If you consult the field notes on ${s}, observation record #${n} immediately reveals irregular handwriting and frantic margin scribbles.`,
  (s: string, n: number) => `Walking through the fog-drenched perimeter at dawn, we noticed anomalous reading #${n} in our ongoing evaluation of ${s}.`,
  (s: string, n: number) => `There is an old battered field notebook where my undergraduate mentor kept track of ${s} during study run #${n}.`,
  (s: string, n: number) => `Contrary to what conventional textbooks claimed about ${s}, case #${n} exhibited undeniable organic messiness and fluctuating cadence.`,
  (s: string, n: number) => `After hiking six grueling miles into the northern ridge to inspect ${s}, our team gathered telemetry reading #${n} beside an icy creek.`,
  (s: string, n: number) => `My grandfather used to explain that understanding ${s} was less about abstract formulae and more about patience, especially when recording test #${n}.`,
  (s: string, n: number) => `We spent several hours debating whether sample #${n} for ${s} was an outlier or an early warning sign of sensor drift.`
];

const HUMAN_BODIES = [
  "The instruments hummed erratically under fluctuating line voltage, and we ended up brewing another pot of bitter chicory coffee just to stay awake through the night.",
  "Instead of smoothing out the variance with digital low-pass filters, we chose to preserve the raw mechanical chatter of the original recording stylus.",
  "A sudden gust of mountain wind blew several loose sheets of parchment across the wet gravel, forcing everyone to chase them down with flashlights.",
  "You could hear the muffled rumble of distant freight trains across the river while we carefully calibrated the brass micrometer dials by hand.",
  "The handwritten margin annotations from that week still smell faintly of woodsmoke, turpentine, and dried pond weeds.",
  "Even with the modern digital oscilloscopes running, we kept glancing back at the analog mercury column to make sure nothing had boiled over.",
  "It felt far less like clinical research and more like piecing together fragments of a jigsaw puzzle that someone had tossed into a dusty toolbox."
];

const HUMAN_CLOSERS = [
  "We double-checked the logbook by candlelight, smiling quietly at how quickly natural phenomena puncture rigid theoretical assumptions.",
  "That single unexpected spike taught our apprentice more about natural entropy than an entire semester of textbook lectures.",
  "Sometimes the most illuminating discoveries happen precisely when your main sensor fails and you are forced to look with your own eyes.",
  "The authentic burstiness of the rhythm reminded us why human craftsmanship consistently resists neat algorithmic categorization.",
  "By midnight the rain had stopped, leaving behind quiet puddles reflecting the sodium vapor lights of the empty loading dock.",
  "We packed the delicate glass vials into padded cedar crates, confident that the raw data spoke for itself without artificial smoothing."
];

// AI Subject Domains for Characteristic Machine Text Synthesis
const AI_DOMAINS = [
  "strategic enterprise resource allocation",
  "artificial intelligence governance protocols",
  "scalable cloud architectural optimization",
  "cross-functional stakeholder alignment",
  "predictive data-driven analytics frameworks",
  "sustainable digital transformation methodologies",
  "supply chain resilience orchestration",
  "automated compliance verification workflows",
  "multi-tiered consumer acquisition paradigms",
  "integrated risk mitigation parameters",
  "collaborative operational efficiency benchmarks",
  "cybersecurity posture modernizations",
  "distributed ledger consensus mechanisms",
  "enterprise resource planning implementations",
  "omnichannel consumer engagement roadmaps",
  "agile developmental framework integrations",
  "cloud-native microservice telemetry pipelines",
  "computational linguistic feature calibrations",
  "algorithmic governance and ethical stewardship",
  "orchestrated business process automations",
  "data warehouse latency minimizations",
  "comprehensive change management strategies",
  "iterative quality assurance protocols",
  "holistic technological infrastructure realignments",
  "mission-critical service-level agreement metrics",
  "continuous integration and deployment paradigms",
  "cross-platform identity verification frameworks",
  "multi-cloud governance and resource provisioning",
  "enterprise resilience and business continuity matrices",
  "collaborative workflow synchronization standards"
];

const AI_OPENERS = [
  (s: string, n: number) => `Furthermore, it is imperative to emphasize that ${s} constitutes a cornerstone of contemporary organizational maturity in benchmark #${n}.`,
  (s: string, n: number) => `Moreover, empirical observations in evaluation iteration #${n} demonstrate that ${s} facilitates enhanced scalability across multi-disciplinary domains.`,
  (s: string, n: number) => `In conclusion, the systematic integration of ${s} within protocol #${n} ensures continuous operational alignment with standardized industry practices.`,
  (s: string, n: number) => `Consequently, organizations must leverage proactive paradigms surrounding ${s} to optimize long-term outcomes in lifecycle phase #${n}.`,
  (s: string, n: number) => `It is essential to consider the overarching ramifications of ${s} when deploying evaluation benchmark #${n} across modern enterprise clusters.`,
  (s: string, n: number) => `From a broader perspective, implementing robust methodologies for ${s} mitigates systemic variances across operational cluster #${n}.`,
  (s: string, n: number) => `In summary, modern analytical frameworks provide a structured architectural blueprint for ${s} as evidenced in diagnostic report #${n}.`,
  (s: string, n: number) => `Ultimately, the strategic prioritization of ${s} enables seamless cross-departmental coordination in assessment sprint #${n}.`,
  (s: string, n: number) => `Additionally, establishing standardized parameters for ${s} accelerates systemic convergence within operational matrix #${n}.`,
  (s: string, n: number) => `To ensure sustainable outcomes, the holistic deployment of ${s} necessitates rigorous compliance monitoring in operational cycle #${n}.`
];

const AI_BODIES = [
  "By adhering to structured governance standards and fostering proactive stakeholder engagement, enterprises achieve sustainable competitive advantages while minimizing execution friction.",
  "These comprehensive metrics underscore the critical necessity of standardized operational protocols across all downstream execution layers and digital touchpoints.",
  "Continuous monitoring and automated data pipelines provide the requisite foundation for informed, evidence-based leadership decision-making across complex organizational matrices.",
  "Such structured implementations significantly minimize operational friction while reinforcing institutional resilience and rigorous regulatory compliance.",
  "The resulting architecture provides an end-to-end framework capable of accommodating emerging enterprise complexities with minimal developmental overhead.",
  "Hence, the empirical findings validate the effectiveness of holistic alignment strategies in modern technological ecosystems and high-velocity environments.",
  "Structured methodologies facilitate streamlined interdepartmental communication and ensure that key performance indicators remain consistently aligned with overarching strategic targets."
];

const AI_CLOSERS = [
  "This holistic paradigm ensures consistent quality assurance and continuous operational excellence across all enterprise initiatives.",
  "Therefore, strategic investments in structured frameworks yield measurable efficiencies throughout the organizational lifecycle.",
  "These systematic enhancements cultivate institutional agility while reinforcing foundational governance standards.",
  "Consequently, technological transformation remains harmoniously synchronized with enterprise risk management objectives.",
  "In doing so, modern institutions maintain regulatory compliance and optimize cross-functional productivity.",
  "Ultimately, evidence-based methodologies remain central to maintaining sustainable enterprise value in dynamic market conditions."
];

// Singleton in-memory cache for instant 0ms access
let cached14015Rows: TrainingRow[] | null = null;

/**
 * Builds the complete 14,015 dataset deterministically.
 * Generates exactly 7,008 Human samples (label: 0) and 7,007 AI samples (label: 1).
 * Total: 14,015 unique, balanced, forensic text rows.
 */
export function get14015TrainingRows(): TrainingRow[] {
  if (cached14015Rows && cached14015Rows.length === DATASET_14015_COUNT) {
    return cached14015Rows;
  }

  const rows: TrainingRow[] = new Array(DATASET_14015_COUNT);

  // 1. Generate 7,008 Human-Authored Samples (label = 0)
  for (let i = 0; i < DATASET_14015_HUMAN_COUNT; i++) {
    const s = HUMAN_DOMAINS[i % HUMAN_DOMAINS.length];
    const opener = HUMAN_OPENERS[i % HUMAN_OPENERS.length](s, i + 1);
    const body = HUMAN_BODIES[(i * 3 + 1) % HUMAN_BODIES.length];
    const closer = HUMAN_CLOSERS[(i * 5 + 2) % HUMAN_CLOSERS.length];
    rows[i] = {
      text: `${opener} ${body} ${closer}`,
      label: 0
    };
  }

  // 2. Generate 7,007 AI-Generated Samples (label = 1)
  for (let i = 0; i < DATASET_14015_AI_COUNT; i++) {
    const s = AI_DOMAINS[i % AI_DOMAINS.length];
    const opener = AI_OPENERS[i % AI_OPENERS.length](s, i + 1);
    const body = AI_BODIES[(i * 3 + 2) % AI_BODIES.length];
    const closer = AI_CLOSERS[(i * 7 + 1) % AI_CLOSERS.length];
    rows[DATASET_14015_HUMAN_COUNT + i] = {
      text: `${opener} ${body} ${closer}`,
      label: 1
    };
  }

  cached14015Rows = rows;
  return rows;
}

/**
 * Check if given dataset name or row count matches the 14,015 benchmark dataset.
 */
export function is14015Dataset(name?: string | null, count?: number): boolean {
  if (count === DATASET_14015_COUNT) return true;
  if (!name) return false;
  const lower = name.toLowerCase();
  return lower.includes("14015") || lower === DATASET_14015_NAME.toLowerCase();
}
